/**
 * BillCard — item della lista bollette.
 *
 * Replica del componente mobile (BillCard.js):
 *   - codice bolletta + periodo "dal-al" a sinistra
 *   - importo grande + badge stato con icona a destra
 *   - footer (border-top): "Scadenza: XX/XX/XXXX" + "NNN kWh"
 *   - intera card cliccabile (chiave per dettaglio)
 */

import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  Undo2,
  type LucideIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { parseBackendDate } from '@/lib/dates'
import type { Bolletta } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'

type StateKey =
  | 'pagata'
  | 'in scadenza'
  | 'da pagare'
  | 'scaduta'
  | 'rateizzata'
  | 'stornata'

// Mapping stato → variant cromatica + icona (identici al mobile).
const STATE_META: Record<StateKey, { color: string; icon: LucideIcon }> = {
  pagata: { color: 'text-success bg-success/15', icon: CheckCircle2 },
  'in scadenza': { color: 'text-warning-foreground bg-warning/20', icon: Clock },
  'da pagare': { color: 'text-secondary bg-secondary/15', icon: CreditCard },
  scaduta: { color: 'text-destructive bg-destructive/12', icon: AlertCircle },
  rateizzata: { color: 'text-tertiary-foreground bg-tertiary/20', icon: Calendar },
  stornata: { color: 'text-muted-foreground bg-muted', icon: Undo2 },
}

function formatPeriod(invoice: Bolletta): string {
  const dal = parseBackendDate(invoice.dal)
  const al = parseBackendDate(invoice.al)
  if (dal && al) {
    const dalMese = capitalize(dal.format('MMM'))
    const alMese = capitalize(al.format('MMM'))
    const anno = al.format('YYYY')
    return dalMese === alMese
      ? `${dalMese} ${anno}`
      : `${dalMese} - ${alMese} ${anno}`
  }
  const em = parseBackendDate(invoice.data_emissione)
  if (em) return `${capitalize(em.format('MMMM'))} ${em.format('YYYY')}`
  return ''
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}

export function BillCard({ invoice }: { invoice: Bolletta }) {
  const statoKey = (invoice.stato ?? '').toLowerCase() as StateKey
  const meta = STATE_META[statoKey] ?? {
    color: 'text-muted-foreground bg-muted',
    icon: FileText,
  }
  const Icon = meta.icon
  const periodLabel = formatPeriod(invoice)
  const codice = invoice.codice ?? ''
  const consumo = invoice.consumo != null ? Math.round(Number(invoice.consumo)) : null

  return (
    <Link to={`/bollette/${encodeURIComponent(codice)}`} className="block">
      <Card interactive className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold text-foreground">
              Bolletta {codice || '—'}
            </p>
            {periodLabel && (
              <p className="mt-0.5 text-sm text-muted-foreground">{periodLabel}</p>
            )}
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            <span className="text-lg font-bold tabular-nums text-foreground">
              {Number(invoice.imponibile || 0).toFixed(2).replace('.', ',')} €
            </span>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
                meta.color,
              )}
            >
              <Icon className="h-3 w-3" />
              {invoice.stato || 'sconosciuto'}
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
          <span>Scadenza: {invoice.data_scadenza || '—'}</span>
          {consumo != null && <span>{consumo} kWh</span>}
        </div>
      </Card>
    </Link>
  )
}
